import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Search,
  Filter,
  Eye,
  Edit,
  Mail,
  UserPlus,
  Calendar,
  MapPin,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronDown,
  Download,
  RefreshCw,
  X,
  Trash2,
  CheckSquare,
  Square
} from 'lucide-react';
import adminApiService from '../../services/adminApiService';

const ConsultationsModule = () => {
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [filters, setFilters] = useState({
    serviceType: 'all',
    status: 'all',
    dateRange: 'all',
    outcome: 'all'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    loadConsultations();
  }, [filters, pagination.page, pagination.limit]);

  const loadConsultations = async () => {
    try {
      setLoading(true);
      const response = await adminApiService.consultations.getLeads({
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      });
      setConsultations(response.leads || []);
      setPagination(prev => ({
        ...prev,
        total: response.pagination?.total || 0,
        pages: response.pagination?.pages || 0
      }));
    } catch (error) {
      console.error('Error loading consultations:', error);
      setConsultations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedLeads.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedLeads.length} consultation lead(s)?`)) {
      return;
    }

    try {
      await adminApiService.consultations.bulkDelete(selectedLeads);
      setSelectedLeads([]);
      loadConsultations();
    } catch (error) {
      console.error('Error bulk deleting consultations:', error);
    }
  };

  const handleExportCSV = async () => {
    try {
      const blob = await adminApiService.consultations.exportToCSV(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `consultation_leads_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting CSV:', error);
    }
  };

  const handleSelectLead = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleSelectAll = () => {
    if (selectedLeads.length === consultations.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(consultations.map(lead => lead._id));
    }
  };

  const handleOutcomeUpdate = async (consultationId, outcome) => {
    try {
      // Update status based on outcome
      const status = outcome === 'eligible_to_proceed' ? 'booked' : 'completed';
      
      await adminApiService.consultations.updateStatus(consultationId, {
        status,
        notes: `Outcome set to: ${outcome.replace('_', ' ')}`
      });
      
      // Update local state
      setConsultations(prev => 
        prev.map(consultation => 
          consultation._id === consultationId 
            ? { ...consultation, outcome, status }
            : consultation
        )
      );
      
      // Refresh data
      loadConsultations();
    } catch (error) {
      console.error('Error updating outcome:', error);
      // Show error message to user
    }
  };

  const handleAdvisorAssignment = async (consultationId, advisorId) => {
    try {
      await adminApiService.consultations.updateStatus(consultationId, {
        notes: advisorId ? `Assigned advisor: ${advisorId}` : 'Advisor unassigned'
      });
      
      // Update local state
      setConsultations(prev => 
        prev.map(consultation => 
          consultation._id === consultationId 
            ? { ...consultation, assignedAdvisor: advisorId }
            : consultation
        )
      );
      
      // Refresh data
      loadConsultations();
    } catch (error) {
      console.error('Error assigning advisor:', error);
      // Show error message to user
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'lead': return 'bg-blue-100 text-blue-800';
      case 'booked': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getOutcomeColor = (outcome) => {
    switch (outcome) {
      case 'eligible_to_proceed': return 'bg-green-100 text-green-800';
      case 'eligible_with_conditions': return 'bg-yellow-100 text-yellow-800';
      case 'not_eligible': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getServiceTypeIcon = (serviceType) => {
    return serviceType === 'study-abroad' ? '🎓' : '💼';
  };

  const filteredConsultations = consultations.filter(consultation => {
    const matchesSearch = consultation.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         consultation.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesServiceType = filters.serviceType === 'all' || consultation.serviceType === filters.serviceType;
    const matchesStatus = filters.status === 'all' || consultation.status === filters.status;
    const matchesOutcome = filters.outcome === 'all' || consultation.outcome === filters.outcome;
    
    return matchesSearch && matchesServiceType && matchesStatus && matchesOutcome;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#202356]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Consultations</h2>
          <p className="text-gray-600 mt-1">Manage consultation leads and outcomes</p>
        </div>
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            <Download size={16} />
            Export
          </button>
          <button 
            onClick={loadConsultations}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
              />
            </div>
          </div>

          {/* Action Buttons */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Filter size={16} />
              Filters
              <ChevronDown size={16} className={`transform transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
            
            {selectedLeads.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  {selectedLeads.length} selected
                </span>
                <button
                  onClick={handleBulkDelete}
                  className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <Trash2 size={16} />
                  Delete Selected
                </button>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <select
              value={pagination.limit}
              onChange={(e) => setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
            >
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
            
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download size={16} />
              Export CSV
            </button>
            
            <button
              onClick={loadConsultations}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        </div>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Service Type</label>
              <select
                value={filters.serviceType}
                onChange={(e) => setFilters(prev => ({ ...prev, serviceType: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
              >
                <option value="all">All Types</option>
                <option value="study-abroad">Study Abroad</option>
                <option value="work-abroad">Work Abroad</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
              >
                <option value="all">All Status</option>
                <option value="lead">Lead</option>
                <option value="booked">Booked</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Outcome</label>
              <select
                value={filters.outcome}
                onChange={(e) => setFilters(prev => ({ ...prev, outcome: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
              >
                <option value="all">All Outcomes</option>
                <option value="eligible_to_proceed">Eligible to Proceed</option>
                <option value="eligible_with_conditions">Eligible with Conditions</option>
                <option value="not_eligible">Not Eligible</option>
              </select>
            </div>
          </motion.div>
        )}
      </div>

      {/* Consultations Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left">
                  <button
                    onClick={handleSelectAll}
                    className="flex items-center justify-center w-5 h-5 border border-gray-300 rounded hover:bg-gray-100"
                  >
                    {selectedLeads.length === consultations.length && consultations.length > 0 ? (
                      <CheckSquare size={16} className="text-blue-600" />
                    ) : (
                      <Square size={16} className="text-gray-400" />
                    )}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lead
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Service
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timeline
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Outcome
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Advisor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {consultations.map((consultation, index) => (
                <motion.tr
                  key={consultation._id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="hover:bg-gray-50"
                >
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleSelectLead(consultation._id)}
                      className="flex items-center justify-center w-5 h-5 border border-gray-300 rounded hover:bg-gray-100"
                    >
                      {selectedLeads.includes(consultation._id) ? (
                        <CheckSquare size={16} className="text-blue-600" />
                      ) : (
                        <Square size={16} className="text-gray-400" />
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-gray-900">{consultation.name}</div>
                      <div className="text-sm text-gray-500">{consultation.email}</div>
                      <div className="text-xs text-gray-400">{consultation.phone || 'N/A'}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{getServiceTypeIcon(consultation.serviceType)}</span>
                      <div>
                        <div className="text-sm font-medium text-gray-900 capitalize">
                          {consultation.serviceType.replace('-', ' ')}
                        </div>
                        <div className="text-xs text-gray-500 capitalize">
                          {consultation.consultationType.replace('-', ' ')}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <div className="flex items-center gap-1 text-gray-900">
                        <MapPin size={12} />
                        {consultation.countryOfResidence}
                      </div>
                      <div className="flex items-center gap-1 text-gray-500">
                        <span>→</span>
                        {consultation.targetCountry}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1 text-sm text-gray-900">
                      <Clock size={12} />
                      <span className="capitalize">{consultation.timeline.replace('-', ' ')}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(consultation.status)}`}>
                      {consultation.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {consultation.status === 'completed' ? (
                      <span className="text-sm text-gray-600">Completed</span>
                    ) : (
                      <span className="text-gray-400 text-sm">Not set</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {consultation.assignedAdvisor ? (
                      <div className="text-sm text-gray-900">{consultation.assignedAdvisor}</div>
                    ) : (
                      <button className="text-sm text-blue-600 hover:text-blue-800">Assign</button>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setSelectedConsultation(consultation);
                          setShowDetailsModal(true);
                        }}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => consultation.calendlyLink && window.open(consultation.calendlyLink, '_blank')}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                        title="Open Calendly"
                        disabled={!consultation.calendlyLink}
                      >
                        <Calendar size={16} />
                      </button>
                      <button
                        onClick={() => window.open(`mailto:${consultation.email}`, '_blank')}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title="Send Email"
                      >
                        <Mail size={16} />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {consultations.length === 0 && !loading && (
          <div className="text-center py-8">
            <AlertCircle className="text-gray-400 mx-auto mb-2" size={48} />
            <p className="text-gray-500">No consultations found matching your criteria</p>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                disabled={pagination.page === 1}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Previous
              </button>
              
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                  const pageNum = i + 1;
                  const isActive = pageNum === pagination.page;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                      className={`px-3 py-1 rounded-md ${isActive ? 'bg-blue-600 text-white' : 'border border-gray-300 hover:bg-gray-100'}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.min(pagination.pages, prev.page + 1) }))}
                disabled={pagination.page === pagination.pages}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedConsultation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">Consultation Details</h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Lead Information */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Lead Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-500">Name</label>
                    <p className="font-medium">{selectedConsultation.name}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Email</label>
                    <p className="font-medium">{selectedConsultation.email}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Phone</label>
                    <p className="font-medium">{selectedConsultation.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Created</label>
                    <p className="font-medium">{new Date(selectedConsultation.createdAt).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>

              {/* Consultation Details */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Consultation Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-500">Service Type</label>
                    <p className="font-medium capitalize">{selectedConsultation.serviceType.replace('-', ' ')}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Consultation Type</label>
                    <p className="font-medium capitalize">{selectedConsultation.consultationType.replace('-', ' ')}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Country of Residence</label>
                    <p className="font-medium">{selectedConsultation.countryOfResidence}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Target Country</label>
                    <p className="font-medium">{selectedConsultation.targetCountry}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Timeline</label>
                    <p className="font-medium capitalize">{selectedConsultation.timeline.replace('-', ' ')}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Calendly Link</label>
                    <button
                      onClick={() => window.open(selectedConsultation.calendlyLink, '_blank')}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      Open Calendly
                    </button>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="text-sm text-gray-500">Notes</label>
                <p className="text-gray-900">{selectedConsultation.notes}</p>
              </div>

              {/* Actions */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Actions</h4>
                
                {/* Status Update */}
                <div className="mb-4">
                  <label className="text-sm text-gray-500 mb-2 block">Update Status</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => handleOutcomeUpdate(selectedConsultation._id, 'eligible_to_proceed')}
                      className="flex items-center justify-center gap-2 p-3 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg transition-colors"
                      disabled={selectedConsultation.status === 'booked'}
                    >
                      <CheckCircle size={16} />
                      Mark as Booked
                    </button>
                    <button
                      onClick={() => handleOutcomeUpdate(selectedConsultation._id, 'not_eligible')}
                      className="flex items-center justify-center gap-2 p-3 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg transition-colors"
                      disabled={selectedConsultation.status === 'completed'}
                    >
                      <XCircle size={16} />
                      Mark as Completed
                    </button>
                  </div>
                </div>

                {/* Advisor Assignment */}
                <div>
                  <label className="text-sm text-gray-500 mb-2 block">Assign Advisor</label>
                  <select
                    value={selectedConsultation.assignedAdvisor || ''}
                    onChange={(e) => handleAdvisorAssignment(selectedConsultation._id, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                  >
                    <option value="">Select Advisor</option>
                    <option value="Sarah Johnson">Sarah Johnson</option>
                    <option value="Mike Wilson">Mike Wilson</option>
                    <option value="Emily Davis">Emily Davis</option>
                    <option value="David Brown">David Brown</option>
                  </select>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ConsultationsModule;
